import { useMemo } from 'react';
import { useTenant } from '@/contexts/TenantContext';
import { getTenantSlug, rememberedTenantSlug, tenantUrl } from '@/utils/subdomain';

/**
 * The tenant slug as best the client can resolve it: hostname first, then
 * the tenant record, then the cross-surface cookie. Null on the neutral
 * capture host (app.systemdox.com) until the tenant loads — or forever,
 * for workspaces that have no slug yet.
 */
export function useTenantSlug(): string | null {
  const { tenant } = useTenant();
  return getTenantSlug() ?? tenant?.slug ?? rememberedTenantSlug() ?? null;
}

/**
 * Absolute URL builder onto the tenant's Build console — for Build-only
 * routes (the spec editors, Settings) linked from the Docs page, which lives
 * on the tenant's launch home. Null when no slug is resolvable: there is no
 * neutral console host, and a relative path would dead-end on the launch
 * host's catch-all — callers hide the console affordance instead.
 */
export function useConsoleUrl(): ((path: string) => string) | null {
  const slug = useTenantSlug();
  return useMemo(() => (slug ? (path: string) => tenantUrl(slug, path) : null), [slug]);
}
